import React from 'react';
import { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

//Context

import { useGlobalAdminSettings } from '../../context/AdminSettingsProvider';
import { useGlobalSuperAdminSettings } from '../../context/SuperAdminSettingsProvider';

//Hooks
import useAuth from '../../hooks/useAuth';

//Styling
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faShoePrints, faNewspaper, faEnvelope, faEllipsisH, faSignOutAlt, faSignInAlt, faUsers, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

//Components
import BottomSheet from './BottomSheet';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import SmallFollowNotification from '../navbarComponents/SmallFollowNotification';
import RedNotification from '../navbarComponents/RedNotification';

//Util functions
import useLogout from '../../hooks/useLogout';

const NavBarBottom = ({ isNavOpen, toggleNav, isFollowNotification, setIsFollowNotification, hasNewMessages, hasMessagesAtAll, hasPostReports, setHasPostReports, hasCommentsReports, setHasCommentsReports, profilePictureKey, onOpenDemoModal }) => {

  // console.log(hasPostReports, hasCommentsReports)

  const { t } = useTranslation();

  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const FRONTEND = process.env.REACT_APP_FRONTEND_URL;

  const { auth, authLoaded } = useAuth();

  const isAuthenticated = authLoaded && !!auth?.userId;
  const roles = auth?.roles ?? [];
  const loggedInUser = auth?.userId

  const isSuperAdmin = roles.includes('SuperAdmin');
  const isAdmin = roles.includes('Admin');
  const isModerator = roles.includes('Moderator');
  const isAdminNotSuperAdmin = isAdmin && !isSuperAdmin;
  const isAdminAndSuperAdmin = isAdmin && isSuperAdmin;
  const canEditSchool = loggedInUser !== undefined && loggedInUser <= 2;




  const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

  // console.log('auth', auth)

  // const hasMessagesAtAll =

  const { adminSettings, isLoading } = useGlobalAdminSettings();
  const { superAdminSettings } = useGlobalSuperAdminSettings();

  const navigate = useNavigate();
  const [activeSheet, setActiveSheet] = useState(null);
  const [customRoles, setCustomRoles] = useState();
  const [subSection, setSubSection] = useState(null);
  const logout = useLogout();

  // Navigate to a specific path
  const handleNavigate = (path) => {
    navigate(path);

    if (isNavOpen && window.innerWidth <= 580) {
      toggleNav();
    }

    setActiveSheet(null)
  };

  const fetchCustomRoles = async () => {
    try {
      const response = await axios.get('/custom-roles-public');
      setCustomRoles(response.data);
    } catch (err) {
      console.error('Failed to fetch custom roles', err);
    }
  };

  useEffect(() => {
    fetchCustomRoles();
  }, []);

  const getSubItemsForSection = (section) => {
    switch (section) {
      case 'moderator':
        return [
          { label: t('navbar.moderatePosts'), path: '/moderator/posts', showDot: hasPostReports },
          { label: t('navbar.moderateComments'), path: '/moderator/comments', showDot: hasCommentsReports },
          { label: t('navbar.hiddenPosts'), path: '/moderator/hidden/posts' },
          { label: t('navbar.hiddenComments'), path: '/moderator/hidden/comments' },
          { label: t('navbar.moderationHistoryPosts'), path: '/moderator/posts/history' },
          { label: t('navbar.moderationHistoryComments'), path: '/moderator/comments/history' },
        ];

      case 'users':
        const userItems = [];

        // Conditionally add following, followers, and pending requests based on adminSettings.allowFollow
        if (adminSettings.showSocialFeature) {
          userItems.push(
            { label: t('navbar.allUsers'), path: '/social/allusers' }
          );
        }


        if (adminSettings.allowFollow) {
          userItems.push(
            { label: t('navbar.following'), path: '/social/following' },
            { label: t('navbar.followers'), path: '/social/followers' },
            // HERE? Pending Requests notification
            { label: t('navbar.pendingRequests'), path: '/social/pending' }
          );
        }

        if (adminSettings.showSocialFeature) {
          userItems.push(
            { label: t('navbar.muted'), path: '/social/muted' }
          );
        }

        return userItems;
      case 'admin':
        const adminItems = [];

        if (isSuperAdmin) {
          adminItems.push({ label: t('navbar.superAdminSettings'), path: '/admin/superadmin/setup' });
        }

        if (isAdminNotSuperAdmin || isAdminAndSuperAdmin || isSuperAdmin) {
          adminItems.push(
            { label: t('navbar.adminSettings'), path: '/admin/admin/setup' },
            { label: t('navbar.adminUsers'), path: '/admin/users' },
            { label: t('navbar.adminRoles'), path: '/admin/roles' },
            { label: t('navbar.adminRolesHistory'), path: '/admin/superadmin/roleadminhistory' },
            { label: t('navbar.roleChangeLog'), path: '/admin/superadmin/rolechangelog' },
            { label: t('navbar.loginHistory'), path: '/admin/superadmin/loginhistory' },
            { label: t('navbar.subscriptions'), path: '/admin/adminsubscription' },
            { label: t('navbar.stepLogs'), path: '/admin/steplog' }
          );
        }

        if (canEditSchool) {
          adminItems.push({ label: t('navbar.schoolAdmin'), path: '/admin/homeadmin' })
        }


        if (isSuperAdmin) {
          adminItems.push({ label: t('navbar.adminoverride'), path: '/admin/adminoverride' });
        }

        return adminItems;

      default:
        return [];
    }
  };

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <>
      <footer className='navbar-bottom'>
        <div className='navbar-bottom-container'>
          <div onClick={() => {
            handleNavigate('/')
            setActiveSheet(null)
          }}>
            <FontAwesomeIcon icon={faHome} /></div>

          <div
            onClick={async () => {
              await fetchCustomRoles();
              setActiveSheet(null)
              setActiveSheet('roles');
            }}
          >  <FontAwesomeIcon icon={faShoePrints} /></div>

          {adminSettings.showPostsFeature && <div onClick={() => {
            setActiveSheet(null)
            handleNavigate('/posts')
          }}>   <FontAwesomeIcon icon={faNewspaper} /></div>}



          {isAuthenticated && superAdminSettings.showMessagesFeature &&
            adminSettings.showMessagesFeature &&
            !hasNewMessages &&
            (
              (superAdminSettings.showSocialFeature && adminSettings.showSocialFeature) ||
              hasMessagesAtAll
            ) && (

              <div onClick={() => {
                setActiveSheet(null)
                handleNavigate('/messages')
              }}>   <FontAwesomeIcon icon={faEnvelope} /></div>
            )}


          {isAuthenticated && superAdminSettings.showMessagesFeature &&
            adminSettings.showMessagesFeature &&
            hasNewMessages &&
            (
              (superAdminSettings.showSocialFeature && adminSettings.showSocialFeature) ||
              hasMessagesAtAll
            ) && (


              // hasMessagesAtAll &&
              <div className="new-message-bell-container" onClick={() => {
                setActiveSheet(null)
                handleNavigate('/messages')
              }}>
                <FontAwesomeIcon
                  className="faBell-new-message"
                  icon={faEnvelope} />
                <span className="new-message-notification-dot"></span>
              </div>
            )}


          {/* HERE IS FOLLOW NOTIFICATION CONDITIONAL DISPLAY */}
          {isAuthenticated && adminSettings.showSocialFeature && (
            <div
              onClick={() => {
                setActiveSheet(null);
                setActiveSheet('users');
              }}
            >
              {!isFollowNotification
                ? <FontAwesomeIcon icon={faUsers} />
                : <SmallFollowNotification />
              }
            </div>
          )}



          {loggedInUser && (
            <div
              className='navbar-bottom-small-img-container'
              onClick={() => {
                setActiveSheet(null);
                handleNavigate('/profile/myaccount');
              }}
            >
              <img
                className="navbar-bottom-small-img"
                src={`${BACKEND}/media/profile_pictures/${loggedInUser}/profilePicture.jpg?v=${profilePictureKey}`}
                alt="Profile"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/profilePicture.jpg';
                }}
              />
            </div>
          )}




          {((hasPostReports || hasCommentsReports) && isModerator) ?
            <div
              className="bell-container"
              onClick={() => {
                setActiveSheet(null)
                setActiveSheet('ellipsis');
              }}>

              <FontAwesomeIcon
                className="faBell-follow-request"
                icon={faEllipsisH} />
              <span className="notification-dot"></span>
            </div>
            :
            <div
              onClick={() => {
                setActiveSheet(null)
                setActiveSheet('ellipsis');
              }}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </div>
          }

          {IS_DEMO_MODE && (
            <div
              onClick={(e) => {
                e.stopPropagation(); // 🔥 prevents ellipsis click
                onOpenDemoModal();
              }}
              title="Demo mode — resets daily at 4:00 a.m. (Vancouver time)"
            >
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: '#f59e0b' }}
              />
            </div>
          )}


        </div>
      </footer >

      {/* Bottom sheet for roles */}
      <BottomSheet isOpen={activeSheet === 'roles'} onClose={() => setActiveSheet(null)}>
        <h4 style={{ marginBottom: '0.5em' }}>{t('navbar.selectProtectedRoute')}</h4>



        <ul>
          {customRoles?.map((role) => {
            const userHasAccess = auth?.roles?.includes(role.role_name);

            if (!role.listed_for_all && !userHasAccess) {
              return null;
            }

            return (
              <li
                key={role.role_id}
                style={{ padding: '10px 0px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                onClick={() => handleNavigate(`/steps/${role.role_slug}`)}

              >
                {role.role_name}
              </li>

            );
          })}
        </ul>

        <li
          style={{ padding: '10px 0px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
          onClick={() => handleNavigate(`/steps/favourites`)}

        >
          {t('navbar.favouriteSteps')}
        </li>

        {isAdmin && (
          <li
            style={{ padding: '10px 0px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
            onClick={() => handleNavigate(`/admin/steps/`)}
          >
            {t('navbar.adminSteps')}
          </li>
        )}

        {isAdmin && (
          <li
            style={{ padding: '10px 0px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
            onClick={() => handleNavigate(`/admin/orphaned-steps/`)}
          >
            {t('navbar.adminOrphaned')}
          </li>
        )}

        {/* Admin "Add +" button */}

        {isAdmin && (
          <div
            className='navbarBottomStepButton'
            onClick={() => handleNavigate('/steps/add')}
          >
            {t('navbar.addNewStep')}
          </div>
        )}

        {isAdmin && (
          <div
            className='navbarBottomStepButton'
            onClick={() => handleNavigate('/admin/roles')}
          >
            {t('navbar.addNewLevel')}
          </div>
        )}


      </BottomSheet>



      {/* Bottom sheet for social options */}
      <BottomSheet isOpen={activeSheet === 'users'} onClose={() => setActiveSheet(null)}>
        <h4 style={{ marginBottom: '1em' }}>Users</h4>
        <ul>
          {getSubItemsForSection('users').map((item, idx) => (
            <li
              key={idx}
              style={{ padding: '10px 0', borderBottom: '1px solid #eee', cursor: 'pointer' }}
              onClick={() => {
                handleNavigate(item.path);
                setActiveSheet(null);
              }}
            >

              <span>{item.label}</span>
              {isFollowNotification && item.label === 'Pending requests' && <RedNotification />}


            </li>
          ))}
        </ul>
      </BottomSheet>


      {/* Bottom sheet for Everything else */}
      <BottomSheet isOpen={activeSheet === 'ellipsis'} onClose={() => setActiveSheet(null)}>
        {!subSection && (
          <ul>
            {(() => {
              const ellipsisItems = [];

              if (superAdminSettings?.showSubscriberFeature) {
                ellipsisItems.push({ label: 'Subscriber', icon: null });
              }

              if (isModerator && adminSettings?.showPostsFeature) {
                ellipsisItems.push({ label: t('navbar.moderator'), icon: null });
              }

              if (isAdminNotSuperAdmin || isAdminAndSuperAdmin || isSuperAdmin) {
                ellipsisItems.push({ label: t('navbar.admin'), icon: null });
              }

              if (isAuthenticated) {
                ellipsisItems.push({ label: t('profile.signOut'), icon: faSignOutAlt });
              } else {
                ellipsisItems.push({ label: t('profile.signIn'), icon: faSignInAlt });
              }


              return ellipsisItems.map((item, index) => (

                <li
                  key={index}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  onClick={() => {
                    const label = item.label;

                    const signOutLabel = t('profile.signOut');
                    const signInLabel = t('profile.signIn');
                    const subscriberLabel = t('navbar.subscriber');
                    const moderatorLabel = t('navbar.moderator');
                    const adminLabel = t('navbar.admin');

                    if (label === moderatorLabel) {
                      setSubSection('moderator');
                    } else if (label === adminLabel) {
                      setSubSection('admin');
                    } else if (label === subscriberLabel) {
                      handleNavigate('/subscriber');
                      setActiveSheet(null);
                    } else if (label === signOutLabel) {
                      setIsFollowNotification(false);
                      setHasCommentsReports(false);
                      setHasPostReports(false);
                      setActiveSheet(null);

                      // Delay logout + navigation slightly to allow state update and re-render
                      setTimeout(() => {
                        logout();
                        handleNavigate('/');
                      }, 50);
                    } else if (label === signInLabel) {
                      setActiveSheet(null);
                      handleNavigate('/signin');
                    } else {
                      handleNavigate(`/${label.toLowerCase()}`);
                      setActiveSheet(null);
                    }
                  }}



                >



                  {item.icon && <FontAwesomeIcon icon={item.icon} />}
                  <span>{item.label}</span>

                  {(hasPostReports || hasCommentsReports) && (isModerator && adminSettings?.showPostsFeature) && item.label === 'Moderator' && <div className='in-line-red-dot'></div>}


                </li>
              ));
            })()}
          </ul>
        )}

        {subSection && (
          <div>
            <button onClick={() => setSubSection(null)} className='back-button button-white button-smaller'>
              ← {t('navbar.back')}
            </button>
            <h4 style={{ textTransform: 'capitalize' }}>{t(`navbar.${subSection}`)}</h4>

            <ul>
              {getSubItemsForSection(subSection).map((item, idx) => (
                <li
                  key={idx}
                  style={{
                    padding: '10px 0', borderBottom: '1px solid #eee', cursor: 'pointer', display: 'flex',
                  }}
                  onClick={() => {
                    item.label === 'Moderate posts' && setHasPostReports(false)
                    item.label === 'Moderate comments' && setHasCommentsReports(false)
                    handleNavigate(item.path);
                    setActiveSheet(null);
                    setSubSection(null);
                  }}
                >
                  <span>{item.label}</span>
                  {item.showDot && <span className="in-line-red-dot" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </BottomSheet>




    </>
  );
};

export default NavBarBottom;
